import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { supabase, type Child, type Subject, type SubjectType } from "@/lib/supabase";
import { invalidateChildren } from "@/hooks/useChildren";
import { useAuth } from "@/hooks/useAuth";
import { fetchOwnerSpace } from "@/hooks/queries/useOwnerSpace";
import { isValidPin, isWeakPin } from "@/lib/kidAuth";
import { toast } from "sonner";
import ChildColorPicker from "@/components/ChildColorPicker";
import AvatarUpload from "@/components/AvatarUpload";
import { autoAssignColor } from "@/config/childColors";

// ─── Add / Edit Subject Sheet ─────────────────────────────────────────────────
// Handles children and pets in one component (toys later). Branches on
// `subjectType`: children take birthdate + gender + kid login; pets take a
// required `ai_context` (Mori reads it to write stories) and an optional
// "Gotcha day" mapped onto the same birthdate column.
//
// Lives in src/components/ rather than co-located with MyTribe so the lazy()
// chunk-split in App.tsx works — MemoryMetadataScreen.tsx imports the named
// export here without pulling MyTribe into the main bundle.

export interface AddEditSubjectSheetProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  subjectType: SubjectType;
  existing?: Subject;
  existingSubjects?: Subject[];
  /** Called after a successful create or update. The saved Subject is
   *  passed back so callers (e.g. the capture flow) can auto-select a
   *  freshly-created pet for the current memory. */
  onSaved: (saved?: Subject) => void;
}

// ─── Kid login panel (inside AddEditChildSheet) ───────────────────────────────

function KidLoginPanel({ child, onRevoked }: { child: Child; onRevoked: () => void }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'idle' | 'input' | 'done'>('idle');
  const [newPin, setNewPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [savedPin, setSavedPin] = useState<string | null>(null);

  const pinValid = isValidPin(newPin);
  const pinWeak = pinValid && isWeakPin(newPin);
  const pinOk = pinValid && !pinWeak;

  const handleReset = async () => {
    if (!pinOk) return;
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-reset-child-pin', {
        body: { childId: child.id, newPin },
      });
      if (error) throw error;
      const resp = data as { success?: boolean; error?: string };
      if (!resp.success) throw new Error(resp.error ?? 'Could not reset');
      setSavedPin(newPin);
      setMode('done');
      setNewPin('');
    } catch (e: unknown) {
      toast.error((e as Error).message ?? 'Could not reset PIN');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-revoke-child-account', {
        body: { childId: child.id },
      });
      if (error) throw error;
      const resp = data as { success?: boolean; error?: string };
      if (!resp.success) throw new Error(resp.error ?? t('child.access.panel.revokeError'));
      toast.success(t('child.access.panel.revokeSuccess', { name: child.name }));
      void invalidateChildren();
      onRevoked();
    } catch (e: unknown) {
      toast.error((e as Error).message ?? t('child.access.panel.revokeError'));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <div className="mt-5 rounded-2xl border border-border bg-muted/40 p-4 space-y-3">
      <div className="space-y-0.5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          {t('child.access.panel.title')}
        </p>
        <p className="font-mono text-sm font-semibold text-foreground">{child.username}</p>
      </div>

      {mode === 'idle' && (
        <Button variant="outline" size="sm" className="w-full" onClick={() => setMode('input')}>
          {t('child.access.panel.resetPin')}
        </Button>
      )}

      {mode === 'input' && (
        <div className="space-y-2">
          <Label htmlFor="reset-kid-pin" className="text-xs text-muted-foreground">
            {t('child.access.panel.newPinLabel')}
          </Label>
          <Input
            id="reset-kid-pin"
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={4}
            placeholder="••••"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
            className="h-11 text-lg bg-card text-center tracking-[0.5em]"
            autoFocus
          />
          {pinWeak && (
            <p className="text-xs text-destructive">{t('child.access.fields.pinWeak')}</p>
          )}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <Button variant="ghost" size="sm" onClick={() => { setMode('idle'); setNewPin(''); }}>
              {t('child.access.panel.cancel')}
            </Button>
            <Button size="sm" onClick={handleReset} disabled={!pinOk || submitting}>
              {submitting ? '…' : t('child.access.panel.confirm')}
            </Button>
          </div>
        </div>
      )}

      {mode === 'done' && savedPin && (
        <div className="space-y-2">
          <div className="rounded-xl bg-background border border-border p-3 flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t('child.access.credentials.pin')}</span>
            <span className="font-mono text-lg font-semibold tracking-[0.3em] text-foreground">{savedPin}</span>
          </div>
          <p className="text-xs text-muted-foreground">
            {t('child.access.panel.doneHint', { name: child.name })}
          </p>
          <Button variant="outline" size="sm" className="w-full" onClick={() => { setMode('idle'); setSavedPin(null); }}>
            {t('child.access.panel.close')}
          </Button>
        </div>
      )}

      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
            disabled={revoking}
          >
            {t('child.access.panel.revoke')}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('child.access.panel.revokeTitle', { name: child.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('child.access.panel.revokeDesc', { name: child.name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('child.access.panel.revokeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type ChildGender = "male" | "female" | "neutral";

export function AddEditSubjectSheet({ open, onOpenChange, subjectType, existing, existingSubjects = [], onSaved }: AddEditSubjectSheetProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isPet = subjectType === 'pet';
  const [name, setName] = useState(existing?.name ?? "");
  const [birthdate, setBirthdate] = useState(existing?.birthdate ?? "");
  const [gender, setGender] = useState<ChildGender>(existing?.gender ?? "neutral");
  const [aiContext, setAiContext] = useState(existing?.ai_context ?? "");
  const usedColors = existingSubjects
    .filter((c) => !existing || c.id !== existing.id)
    .map((c) => c.avatar_color)
    .filter((c): c is string => !!c);
  const [avatarColor, setAvatarColor] = useState<string>(
    existing?.avatar_color ?? autoAssignColor(existing?.gender ?? "neutral", existing?.name ?? "", usedColors)
  );
  const [colorManuallySet, setColorManuallySet] = useState(!!existing?.avatar_color);
  const [saving, setSaving] = useState(false);
  const [localAvatarUrl, setLocalAvatarUrl] = useState<string | null>(existing?.avatar_url ?? null);

  // Sync fields when `existing` changes (e.g. opening edit sheet for a different subject)
  useEffect(() => {
    setName(existing?.name ?? "");
    setBirthdate(existing?.birthdate ?? "");
    const g = existing?.gender ?? "neutral";
    setGender(g);
    setAiContext(existing?.ai_context ?? "");
    const existingColor = existing?.avatar_color ?? null;
    setAvatarColor(existingColor ?? autoAssignColor(g, existing?.name ?? "", usedColors));
    setColorManuallySet(!!existingColor);
    setLocalAvatarUrl(existing?.avatar_url ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing, open]);

  // Auto-reassign color when gender changes unless user has manually picked one
  const handleGenderChange = (g: ChildGender) => {
    setGender(g);
    if (!colorManuallySet) {
      setAvatarColor(autoAssignColor(g, name, usedColors));
    }
  };

  const handleColorChange = (hex: string) => {
    setAvatarColor(hex);
    setColorManuallySet(true);
  };

  // Save validation differs by subject type:
  //   child → name + birthdate
  //   pet   → name + ai_context (Mori needs *something* to write a story)
  const canSave = isPet
    ? !!name.trim() && !!aiContext.trim()
    : !!name.trim() && !!birthdate;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);

    // Pets: birthdate optional, gender forced neutral, ai_context required.
    // Children: existing behaviour preserved (birthdate required).
    const payload = isPet
      ? {
          name: name.trim(),
          birthdate: birthdate || null,
          gender: 'neutral' as const,
          avatar_color: avatarColor,
          ai_context: aiContext.trim(),
        }
      : {
          name: name.trim(),
          birthdate,
          gender,
          avatar_color: avatarColor,
          ai_context: aiContext.trim() || null,
        };

    let savedSubject: Subject | undefined;
    if (existing) {
      const { data, error } = await supabase
        .from("children")
        .update(payload)
        .eq("id", existing.id)
        .select()
        .single();
      setSaving(false);
      if (error || !data) {
        console.error("subject update failed", { error, id: existing.id, subjectType });
        toast.error(t('tribe.error.childSave'));
        return;
      }
      savedSubject = { ...data, hasActiveChildAccount: existing.hasActiveChildAccount } as Subject;
    } else {
      if (!user) return;
      // Subjects belong to a space — fetch the owner's space_id first.
      const spaceId = await fetchOwnerSpace(user.id);
      if (!spaceId) { toast.error(t("error.space.notFound")); setSaving(false); return; }
      const { data, error } = await supabase
        .from("children")
        .insert({ space_id: spaceId, subject_type: subjectType, ...payload })
        .select()
        .single();
      setSaving(false);
      if (error || !data) { console.error(error); toast.error(t('tribe.error.childSave')); return; }
      savedSubject = { ...data, hasActiveChildAccount: false } as Subject;
    }
    void invalidateChildren();
    onSaved(savedSubject);
    onOpenChange(false);
  };

  const titleKey = existing
    ? (isPet ? 'tribe.editPet' : 'tribe.editChild')
    : (isPet ? 'tribe.addPet' : 'tribe.addChild');
  const ctaKey = existing
    ? 'tribe.saveChanges'
    : (isPet ? 'tribe.addPetInline' : 'tribe.addChildInline');
  const nameLabelKey = isPet ? 'tribe.pet.nameLabel' : 'tribe.childName';
  const namePlaceholderKey = isPet ? 'tribe.pet.namePlaceholder' : 'tribe.childNamePlaceholder';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-2xl">
        <SheetHeader className="text-left mb-5">
          <SheetTitle>{t(titleKey)}</SheetTitle>
        </SheetHeader>
        {existing && (
          <div className="flex justify-center mb-5">
            <AvatarUpload
              child={{ ...existing, avatar_url: localAvatarUrl }}
              onUploadComplete={(url) => setLocalAvatarUrl(url)}
            />
          </div>
        )}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="subject-name-input">{t(nameLabelKey)}</Label>
            <Input
              id="subject-name-input"
              placeholder={t(namePlaceholderKey)}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-12 text-base bg-card"
            />
          </div>
          {isPet && (
            <div className="space-y-1.5">
              <Label htmlFor="pet-ai-context-input">{t('tribe.pet.aiContextLabel')}</Label>
              <Input
                id="pet-ai-context-input"
                placeholder={t('tribe.pet.aiContextPlaceholder')}
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                className="h-12 text-base bg-card"
              />
              <p className="text-xs text-muted-foreground">{t('tribe.pet.aiContextHelper')}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="subject-birthdate-input">
              {isPet ? t('tribe.pet.gotchaDayLabel') : t('tribe.birthday')}
            </Label>
            <Input
              id="subject-birthdate-input"
              type="date"
              value={birthdate ?? ""}
              onChange={(e) => setBirthdate(e.target.value)}
              className="h-12 text-base bg-card"
            />
          </div>
          {!isPet && (
            <div className="space-y-1.5">
              <Label>{t('tribe.genderLabel')}</Label>
              <ToggleGroup
                type="single"
                value={gender}
                onValueChange={(v) => v && handleGenderChange(v as ChildGender)}
                className="grid grid-cols-3 gap-2"
              >
                <ToggleGroupItem
                  value="female"
                  className="h-12 rounded-md border bg-card text-base data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {t('tribe.genderFemale')}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="male"
                  className="h-12 rounded-md border bg-card text-base data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {t('tribe.genderMale')}
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="neutral"
                  className="h-12 rounded-md border bg-card text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  {t('tribe.genderNeutral')}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
          {!isPet && (
            <div className="space-y-1.5">
              <Label htmlFor="child-ai-context-input">{t('tribe.child.aiContextLabel')}</Label>
              <Input
                id="child-ai-context-input"
                placeholder={t('tribe.child.aiContextPlaceholder')}
                value={aiContext}
                onChange={(e) => setAiContext(e.target.value)}
                className="h-12 text-base bg-card"
              />
              <p className="text-xs text-muted-foreground">{t('tribe.child.aiContextHelper')}</p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>{t('tribe.colour')}</Label>
            <ChildColorPicker value={avatarColor} onChange={handleColorChange} />
          </div>
          <Button
            className="w-full h-12 text-base"
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {saving ? t('tribe.saving') : t(ctaKey)}
          </Button>

          {/* Kid login panel only applies to children — pets never have accounts. */}
          {!isPet && existing?.subject_type === 'child' && existing.username && (
            <KidLoginPanel
              child={existing as Child}
              onRevoked={() => onOpenChange(false)}
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
